import { Box, Button, Typography, Grid, Paper, CircularProgress } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DrawerHeader } from '../../../components/Drawer/DrawerHeader';
import { useEffect, useState } from 'react';
import AutocompleteComponent from '../../../components/layout/Autocomplete';
import {
    useLazyGetPrestadoresQuery,
    useAsignarPrestadorMutation,
    useGetLocalidadesPrestadoresQuery,
    useLazyGetLocalidadesPrestadoresQuery
} from '../../../services/periodicosApi';
import { useSnackbar } from 'notistack';
import { useGetProvincesQuery } from '../../../services/companyApi';
import { tiposPrestadorOptions } from '../consts/periodicosTypes';

interface PeriodicosPrestadorDrawerProps {
    setOpen: (state: string) => void;
    employeeName: string;
    employeeRisk: string;
    selectedIds: string[];
    selectedEmployee: any;
    reloadGrid: (goToNextTab?: boolean) => void;
    reconfirmation?: boolean;
}

const PeriodicosPrestadorDrawer = ({
    selectedIds,
    setOpen,
    reloadGrid,
    reconfirmation,
    selectedEmployee
}: PeriodicosPrestadorDrawerProps) => {
    const { t } = useTranslation('periodicos');
    const [selectedPrestador, setSelectedPrestador] = useState<any>(null);
    const [getPrestadores, { data: prestadores = [], isLoading }] = useLazyGetPrestadoresQuery();
    const { enqueueSnackbar } = useSnackbar();
    const [asignarPrestador, { isLoading: isSaving }] = useAsignarPrestadorMutation();
    const { data: provincesData } = useGetProvincesQuery(null);
    const provincesList = provincesData?.data?.provincias || [];
    const [selectedProvincia, setSelectedProvincia] = useState<{
        id: number;
        nombre: string;
    } | null>(null);
    const [fetchLocalidades, { data: localidadesList = [], isLoading: isLoadingLocalidades }] =
        useLazyGetLocalidadesPrestadoresQuery();
    const [selectedLocalidad, setSelectedLocalidad] = useState<{
        id: number;
        nombre: string;
    } | null>(null);

    const [selectedTipo, setSelectedTipo] = useState<{ id: number; nombre: string } | null>(null);

    useEffect(() => {
        getPrestadores({});
    }, []);

    useEffect(() => {
        const filters: any = {};

        if (selectedProvincia) filters.idProvincia = selectedProvincia.id;
        if (selectedLocalidad) filters.idLocalidad = selectedLocalidad.id;
        if (selectedTipo) filters.tipo = selectedTipo.id;

        getPrestadores(filters);
    }, [selectedProvincia, selectedLocalidad, selectedTipo]);

    useEffect(() => {
        if (selectedProvincia?.id) {
            fetchLocalidades({ idProvincia: selectedProvincia.id });
        } else {
            fetchLocalidades({});
        }

        setSelectedLocalidad(null);
    }, [selectedProvincia]);

    const handleCloseDrawer = () => {
        setOpen('');
    };

    useEffect(() => {
        if (selectedPrestador) {
            setSelectedPrestador(null);
        }
    }, [selectedProvincia, selectedLocalidad, selectedTipo]);

    const handleSave = async () => {
        // if (!selectedPrestador) return;

        // const empleadoId = selectedIds.map((idStr) => parseInt(idStr.split('-')[0], 10));

        // const payload = {
        //     idExamenEmpleados: empleadoId,
        //     idPrestador: selectedPrestador.id
        // };

        // try {
        //     await asignarPrestador(payload).unwrap();
        //     enqueueSnackbar(t('drawer.messages.assignSuccess'), { variant: 'success' });

        //     reloadGrid(true);
        //     setOpen('');
        // } catch (error) {
        //     enqueueSnackbar(t('drawer.messages.assignError'), { variant: 'error' });
        // }
    };

    return (
        <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <Grid container flexDirection="column">
                {reconfirmation ? (
                    <DrawerHeader
                        onIconClick={handleCloseDrawer}
                        text={`${
                            selectedEmployee?.empleado?.nombre || selectedEmployee?.nombre || ''
                        } - ${selectedEmployee?.riesgo?.codigo || ''}  ${
                            selectedEmployee?.riesgo?.descripcion || ''
                        }`}
                    />
                ) : (
                    <DrawerHeader
                        onIconClick={handleCloseDrawer}
                        text={t('drawer.prestador.title')}
                    />
                )}
                {reconfirmation && selectedEmployee?.examenReconfirmacion && (
                    <Grid p={0.8}>
                        <Typography sx={{ fontWeight: 'bold', mb: reconfirmation ? 0 : 2, mt: 4 }}>
                            {t('drawer.exam.subtitle')}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '1rem',
                                font: 'Roboto',
                                fontWeight: '400'
                            }}
                        >
                            {selectedEmployee?.examenReconfirmacion || '-'}{' '}
                        </Typography>
                    </Grid>
                )}
                <Grid container spacing={2} mt={1}>
                    <Grid item xs={12}>
                        <Grid item xs={12}>
                            <AutocompleteComponent
                                label="Provincia"
                                name="provincia"
                                options={provincesList}
                                value={selectedProvincia}
                                onChange={setSelectedProvincia}
                                loading={isLoading}
                                getOptionLabel={(o: any) => o?.nombre || ''}
                                clearable
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AutocompleteComponent
                                label="Localidad"
                                name="localidad"
                                options={localidadesList}
                                value={selectedLocalidad}
                                onChange={setSelectedLocalidad}
                                getOptionLabel={(o: any) => o?.nombre || ''}
                                loading={isLoadingLocalidades}
                                clearable
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <AutocompleteComponent
                                label="Tipo"
                                name="tipo"
                                options={tiposPrestadorOptions(t)}
                                value={selectedTipo}
                                onChange={setSelectedTipo}
                                getOptionLabel={(o: any) => o?.nombre || ''}
                                clearable
                            />
                        </Grid>

                        <AutocompleteComponent
                            label={t('drawer.form.label.prestador')}
                            name="prestador"
                            size="small"
                            options={prestadores || []}
                            value={selectedPrestador}
                            onChange={setSelectedPrestador}
                            clearable
                            loading={isLoading}
                            templateLabel={(option: any) => option?.razonSocial ?? ''}
                        />
                    </Grid>
                </Grid>
                {selectedPrestador && (
                    <Box
                        sx={{
                            mt: 3,
                            p: 2,
                            backgroundColor: '#E0EBED',
                            borderRadius: 2
                        }}
                    >
                        <Typography variant="body1" fontWeight="bold">
                            {selectedPrestador?.razonSocial || '-'}
                        </Typography>

                        <Typography variant="body2">
                            <Box component="span" fontWeight="bold">
                                {t('drawer.form.info.address')}
                            </Box>{' '}
                            {selectedPrestador?.direccion || '-'}
                        </Typography>
                        <Typography variant="body2">
                            <Box component="span" fontWeight="bold">
                                {t('drawer.form.info.cuit')}
                            </Box>{' '}
                            {selectedPrestador?.cuit || '-'}
                        </Typography>
                        <Typography variant="body2">
                            <Box component="span" fontWeight="bold">
                                {t('drawer.form.info.phone')}
                            </Box>{' '}
                            {selectedPrestador?.telefono || '-'}
                        </Typography>
                        <Typography variant="body2">
                            <Box component="span" fontWeight="bold">
                                {t('drawer.form.info.mail')}
                            </Box>{' '}
                            {selectedPrestador?.mail || '-'}
                        </Typography>
                    </Box>
                )}
            </Grid>

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button variant="outlined" onClick={handleCloseDrawer} sx={{ mr: 1 }}>
                    {t('drawer.cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={!selectedPrestador || isSaving}
                    sx={{ textTransform: 'none', minWidth: 100 }}
                >
                    {isSaving ? <CircularProgress size={24} color="inherit" /> : t('drawer.save')}
                </Button>
            </Box>
        </Box>
    );
};

export default PeriodicosPrestadorDrawer;
